import flatMap from "lodash/flatMap";
import zipObject from "lodash/zipObject";
import React from "react";
import {
  CalendarDate,
  FieldNames,
  FieldValidation,
  IBaseInputProps,
  ICheckboxGroupProps,
  ICheckboxGroupValue,
  ICheckboxProps,
  IDataModelValidationResult,
  IDatePickerUniversalProps,
  IGenericRadioOnChangeEvent,
  IInputConfig,
  IOption,
  IOptionGroup,
  IRadioListProps,
  IValidity,
  WatchedFunction,
} from "component-library";
import { ValidationFunction } from "component-library";

import { findClosestOption } from "helpers/findClosestOption";

export type Validators<ComponentModel> = Partial<
  Record<keyof ComponentModel, ValidationFunction[]>
>;

interface IInputManagerInputConfig<ComponentModel>
  extends Omit<IInputConfig, "name"> {
  name: keyof ComponentModel;
}

export type IInputManagerConfig<ComponentModel> =
  IInputManagerInputConfig<ComponentModel>[];

interface IValidationParams<ComponentModel> {
  ignore: (keyof ComponentModel)[];
}

interface IGetValueParams {
  trim: boolean;
}

export interface IInputManagerOptions {}

type IInitialValues<ComponentModel> = Partial<{
  [P in keyof ComponentModel]: ComponentModel[P];
}>;

interface IDropdownInputConfigOptions<T> {
  isFormSubmitted: boolean;
  fallbackWithClosestOption?: boolean;
  onSelect?: (name: string, selectedOption: IOption<T>) => void;
}

interface IDatePickerConfigOptions {
  isFormSubmitted: boolean;
}

const valid: IValidity = {
  invalid: false,
};

class InputManager<ComponentModel extends ArrayLike<unknown>> {
  public validator: WatchedFunction;

  private touchedInputs = new Set<string>();

  public static generateModel<ComponentModel>(
    inputNames: FieldNames<ComponentModel>,
    initialValues?: IInitialValues<ComponentModel>
  ): ComponentModel {
    const keys = Object.keys(inputNames);

    return zipObject(
      keys,
      keys.map((key) =>
        initialValues && key in initialValues ? initialValues[key] : ""
      )
    ) as ComponentModel;
  }

  public static generateValidationDataConfig<ComponentModel>(
    inputNames: FieldNames<ComponentModel>
  ) {
    const keys = Object.keys(inputNames);

    return zipObject(
      keys,
      keys.map(() => valid)
    ) as FieldValidation<ComponentModel>;
  }

  public static generateInputsConfig<ComponentModel>(
    inputNames: FieldNames<ComponentModel>,
    model: ComponentModel,
    validators?: Validators<ComponentModel>
  ) {
    const keys = Object.keys(inputNames);

    return keys.map((key) => ({
      name: key as keyof ComponentModel,
      value: model[key],
      validators: validators?.[key] || [],
    }));
  }

  constructor(
    private getModel: () => ComponentModel,
    private setModel: (model: ComponentModel, callback?: () => void) => void,
    private setValidationResult: (
      validationData: FieldValidation<ComponentModel>
    ) => void,
    private getValidationResult: () => FieldValidation<ComponentModel>,
    public inputs: IInputManagerConfig<ComponentModel>
  ) {
    this.validator = new WatchedFunction(() => undefined);
  }

  public getInputConfig(name: keyof ComponentModel): IBaseInputProps<any, any> {
    const validationData = this.getInputValidationData(name);

    return {
      name: name as string,
      value: this.getValue(name),
      onChange: this.handleInputChange,
      invalid: validationData.invalid,
      validationMessage: validationData.validationMessage,
    };
  }

  public getRadioListConfig<T>(
    name: keyof ComponentModel
  ): Partial<IRadioListProps<T>> {
    const validationData = this.getInputValidationData(name);

    return {
      name: name as string,
      value: this.getValue(name) as unknown as T,
      onChange: this.handleRadioListChange,
      invalid: validationData.invalid,
      validationMessage: validationData.validationMessage,
    };
  }

  public getCheckboxConfig(name: keyof ComponentModel): ICheckboxProps {
    return {
      name: name as string,
      checked: Boolean(this.getValue(name)),
      value: String(this.getValue(name)),
      onChange: this.handleCheckboxChange,
    };
  }

  public getCheckboxGroupConfig(
    name: keyof ComponentModel,
    options: Pick<ICheckboxGroupProps, "label" | "config">
  ): ICheckboxGroupProps {
    const currentValue = this.getValue(name);

    return {
      label: options.label,
      config: options.config,
      name: name as string,
      value: currentValue,
      valueChanged: (value: ICheckboxGroupValue) =>
        this.handleCheckboxGroupChange(name, value, currentValue),
    };
  }

  public getDropdownInputConfig<T>(
    name: keyof ComponentModel,
    list: IOption<T>[] | IOptionGroup<T>[],
    options: IDropdownInputConfigOptions<T>
  ) {
    const currentValue: T = this.getValue(name);
    const validationData = this.getInputValidationData(name);
    const optionList = flatMap<IOption<T> | IOptionGroup<T>, IOption<T>>(
      list,
      (item) => (item as IOptionGroup<T>).options || item
    );
    const selected = optionList.find((item) => item.value === currentValue);
    const inputName = name as string;

    return {
      name,
      selected:
        selected ||
        (options.fallbackWithClosestOption
          ? findClosestOption(currentValue, optionList)
          : undefined),
      optionSelected: (value: IOption<T>) => {
        options.onSelect?.(inputName, value);
        this.handleDropdownInputChange<T>(name, value);
      },
      onClose: () => {
        this.touchedInputs.add(inputName);
        this.validator.call();
      },
      invalid:
        (options.isFormSubmitted || this.touchedInputs.has(inputName)) &&
        validationData.invalid,
      validationMessage: validationData.validationMessage,
    };
  }

  public getDatePickerConfig(
    name: keyof ComponentModel,
    options: IDatePickerConfigOptions
  ): Pick<
    IDatePickerUniversalProps,
    | "name"
    | "value"
    | "valueChanged"
    | "invalid"
    | "validationMessage"
    | "onClose"
  > {
    const validationData = this.getInputValidationData(name);
    const inputName = name as string;

    return {
      name: inputName,
      value: this.getValue(name),
      valueChanged: (date) => this.handleDatePickerInputChange(name, date),
      onClose: () => {
        this.touchedInputs.add(inputName);
        this.validator.call();
      },
      invalid:
        (options.isFormSubmitted || this.touchedInputs.has(inputName)) &&
        validationData.invalid,
      validationMessage: validationData.validationMessage,
    };
  }

  public getValue = (name: keyof ComponentModel, options?: IGetValueParams) => {
    const value = this.getModel()[name] as any;

    return options?.trim && value.trim
      ? value.trim().replace(/ +(?= )/g, "")
      : value;
  };

  public getValues = (): ComponentModel => this.getModel();

  public validate = (result: IDataModelValidationResult<ComponentModel>) => {
    this.setValidationResult(result.validationData);
  };

  public triggerValidation = () => {
    this.validator.call();
  };

  public isValid(options?: IValidationParams<ComponentModel>): boolean {
    const validationResult = this.getValidationResult();
    const ignoreKeys = options?.ignore || [];

    return !Object.keys(validationResult).some(
      (key): boolean =>
        !ignoreKeys.includes(key as keyof ComponentModel) &&
        validationResult[key].invalid
    );
  }

  public setFieldValidationData(
    name: keyof ComponentModel,
    isValid: boolean,
    validationMessage: string
  ): void {
    this.setValidationResult({
      ...this.getValidationResult(),
      [name]: {
        invalid: !isValid,
        validationMessage,
      },
    });
  }

  public updateValidators(validators: Validators<ComponentModel>) {
    this.inputs = this.inputs.map((input) => ({
      ...input,
      validators: validators[input.name as string] || [],
    }));

    this.validator.call();

    this.setModel(this.getModel(), () => {
      this.validator.call();
    });
  }

  public getInputValidationData(name: keyof ComponentModel) {
    return this.getValidationResult()[name];
  }

  public setValue = (
    name: keyof ComponentModel,
    value: unknown,
    callback?: () => void
  ) => {
    this.inputs = this.inputs.map(
      (input: IInputManagerInputConfig<ComponentModel>) => {
        if (input.name === name) {
          return {
            ...input,
            value,
          };
        }

        return input;
      }
    );

    this.setModel(
      {
        ...this.getModel(),
        [name]: value,
      },
      () => {
        this.validator.call();
        callback?.();
      }
    );
  };

  public setValues = (model: ComponentModel) => {
    Object.entries(model).forEach(([key, value]) => {
      this.setValue(key as keyof ComponentModel, value);
    });
  };

  private handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setValue(
      event.target.name as keyof ComponentModel,
      event.target.value
    );
  };

  private handleRadioListChange = <T>(radio: IGenericRadioOnChangeEvent<T>) => {
    this.setValue(radio.name as keyof ComponentModel, radio.value);
  };

  private handleCheckboxChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    this.setValue(
      event.target.name as keyof ComponentModel,
      event.target.checked
    );
  };

  private handleCheckboxGroupChange = (
    name: keyof ComponentModel,
    data: ICheckboxGroupValue,
    currentValue: ICheckboxGroupValue
  ) => {
    this.setValue(name, { ...currentValue, ...data });
  };

  private handleDropdownInputChange = <T>(
    name: keyof ComponentModel,
    item: IOption<T>
  ) => {
    if (item) {
      this.setValue(name, item.value);
    }
  };

  private handleDatePickerInputChange = (
    name: keyof ComponentModel,
    value?: CalendarDate
  ) => {
    this.setValue(name as keyof ComponentModel, value);
  };
}

export default InputManager;
